import { supabase } from '../lib/supabaseClient';
import { secureLog, redactEmail, redactToken } from '../utils/secureLogger';
import type {
  Organization,
  OrganizationMember,
  TeamInvitation,
  TeamInvitationWithDetails,
  SubscriptionPlan,
  UserRole,
  InvitationStatus
} from '../types/team';

// Helper to get current user ID
const getCurrentUserId = async (): Promise<string | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
};

// ============================================================================
// Organization Operations
// ============================================================================

/**
 * Create a new organization when a business account signs up
 */
export const createOrganization = async (
  name: string,
  subscriptionPlan: SubscriptionPlan,
  companyName?: string,
  companyRegistration?: string,
  vatNumber?: string
): Promise<Organization> => {
  const userId = await getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }

  // Determine max members based on plan
  let maxMembers = 1;
  if (subscriptionPlan === 'team') maxMembers = 5;
  if (subscriptionPlan === 'business') maxMembers = 10;

  const { data, error } = await supabase
    .from('organizations')
    .insert({
      name,
      owner_id: userId,
      subscription_plan: subscriptionPlan,
      max_members: maxMembers,
      company_name: companyName,
      company_registration: companyRegistration,
      vat_number: vatNumber,
      is_active: true
    })
    .select()
    .single();

  if (error) throw error;

  // Create organization membership for owner as super_admin
  const { error: memberError } = await supabase
    .from('organization_members')
    .insert({
      organization_id: data.id,
      user_id: userId,
      role: 'super_admin',
      is_active: true
    });

  if (memberError) {
    // Ignore duplicate membership errors (user already member of this org)
    if (memberError.code === '23505') {
      console.log('[createOrganization] User already has membership, continuing...');
    } else {
      console.error('Error creating organization membership:', memberError);
      throw new Error(`Failed to create organization membership: ${memberError.message}`);
    }
  }

  return data;
};

/**
 * Get user's organization (as owner or member)
 * 
 * NoorCAD Compatibility: Prioritizes profiles.organization_id (set by NoorCAD during signup)
 * Falls back to organization_members table if not found in profile
 */
export const getUserOrganization = async (): Promise<Organization | null> => {
  const userId = await getCurrentUserId();
  if (!userId) {
    console.log('[TeamService] getUserOrganization: No user ID');
    return null;
  }

  console.log('[TeamService] getUserOrganization: Starting lookup for user:', userId);

  try {
    // Strategy 1: Check profiles.organization_id (NoorCAD pattern)
    console.log('[TeamService] Strategy 1: Checking profiles.organization_id');
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', userId)
      .maybeSingle();

    if (profileError) {
      console.error('[TeamService] Profile lookup error:', profileError);
    }

    if (profile?.organization_id) {
      console.log('[TeamService] Found organization_id in profile:', profile.organization_id);
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', profile.organization_id)
        .eq('is_active', true)
        .maybeSingle();
      
      if (orgError) {
        console.error('[TeamService] Organization lookup error:', orgError);
      }
      
      if (org) {
        console.log('[TeamService] ✅ Found organization via profile:', org.id);
        return org;
      }
    }

    // Strategy 2: Check if user owns an organization (fallback)
    console.log('[TeamService] Strategy 2: Checking owned organizations');
    const { data: ownedOrg, error: ownedError } = await supabase
      .from('organizations')
      .select('*')
      .eq('owner_id', userId)
      .eq('is_active', true)
      .maybeSingle();

    if (ownedError) {
      console.error('[TeamService] Owned org lookup error:', ownedError);
    }

    if (ownedOrg) {
      console.log('[TeamService] ✅ Found owned organization:', ownedOrg.id);
      return ownedOrg;
    }

    // Strategy 3: Check organization_members table (fallback)
    console.log('[TeamService] Strategy 3: Checking organization_members');
    const { data: membership, error: memberError } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();

    if (memberError) {
      console.error('[TeamService] Membership lookup error:', memberError);
    }

    if (!membership) {
      console.log('[TeamService] No organization found for user');
      return null;
    }

    console.log('[TeamService] Found membership, fetching organization:', membership.organization_id);
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', membership.organization_id)
      .single();

    if (orgError) {
      console.error('[TeamService] Final org lookup error:', orgError);
      return null;
    }
    
    console.log('[TeamService] ✅ Found organization via membership:', org.id);
    return org;
  } catch (error) {
    console.error('[TeamService] getUserOrganization exception:', error);
    return null;
  }
};

/**
 * Check if current user is super admin of their organization
 */
export const isSuperAdmin = async (organizationId?: string): Promise<boolean> => {
  const userId = await getCurrentUserId();
  if (!userId) return false;

  const orgId = organizationId || (await getUserOrganization())?.id;
  if (!orgId) return false;

  // Check if user is owner
  const { data: org } = await supabase
    .from('organizations')
    .select('owner_id')
    .eq('id', orgId)
    .single();

  if (org?.owner_id === userId) return true;

  // Check if user has super_admin role
  const { data: member } = await supabase
    .from('organization_members')
    .select('role')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .eq('is_active', true)
    .single();

  return member?.role === 'super_admin';
};

/**
 * Check if current user is team leader of their organization
 */
export const isTeamLeader = async (organizationId?: string): Promise<boolean> => {
  const userId = await getCurrentUserId();
  if (!userId) return false;

  const orgId = organizationId || (await getUserOrganization())?.id;
  if (!orgId) return false;

  const { data: member } = await supabase
    .from('organization_members')
    .select('role')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .eq('is_active', true)
    .single();

  return member?.role === 'team_leader';
};

/**
 * Check if current user can view all team files (super_admin OR team_leader)
 */
export const canViewAllFiles = async (organizationId?: string): Promise<boolean> => {
  const userId = await getCurrentUserId();
  if (!userId) return false;

  const orgId = organizationId || (await getUserOrganization())?.id;
  if (!orgId) return false;

  const { data: member } = await supabase
    .from('organization_members')
    .select('role')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .eq('is_active', true)
    .single();

  return member?.role === 'super_admin' || member?.role === 'team_leader';
};

/**
 * Check if current user can manage team (only super_admin)
 */
export const canManageTeam = async (organizationId?: string): Promise<boolean> => {
  return await isSuperAdmin(organizationId);
};

/**
 * Get current user's role in their organization
 */
export const getUserRole = async (organizationId?: string): Promise<UserRole | null> => {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const orgId = organizationId || (await getUserOrganization())?.id;
  if (!orgId) return null;

  // Check if user is owner (they're super_admin)
  const { data: org } = await supabase
    .from('organizations')
    .select('owner_id')
    .eq('id', orgId)
    .single();

  if (org?.owner_id === userId) return 'super_admin';

  // Check member role
  const { data: member } = await supabase
    .from('organization_members')
    .select('role')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .eq('is_active', true)
    .single();

  return member?.role || null;
};

/**
 * Check if current user can create surveys (all except team_admin)
 */
export const canCreateSurveys = async (): Promise<boolean> => {
  const userId = await getCurrentUserId();
  if (!userId) return false;

  const { data: member } = await supabase
    .from('organization_members')
    .select('role')
    .eq('user_id', userId)
    .eq('is_active', true)
    .single();

  return member?.role !== 'team_admin';
};

// ============================================================================
// Team Member Operations
// ============================================================================

/**
 * Get all members of user's organization
 */
export const getOrganizationMembers = async (): Promise<OrganizationMember[]> => {
  const org = await getUserOrganization();
  if (!org) return [];

  // Use RPC function to get members with user details
  const { data, error } = await supabase
    .rpc('get_organization_members', { org_id: org.id });

  if (error) throw error;
  return data || [];
};

/**
 * Remove a team member (super admin only)
 * This includes:
 * - All stored files in Supabase Storage
 * - All database records (surveys, properties, etc.)
 * - Removal from organization
 * - User account deletion (if no other memberships)
 */
export const removeMember = async (memberId: string): Promise<void> => {
  secureLog.info('[removeMember] 🗑️ Starting complete member deletion');
  
  // Step 1: Call SQL function to delete from database and get user email
  secureLog.info('[removeMember] Step 1: Deleting from database and auth...');
  const { error, data } = await supabase
    .rpc('delete_team_member_completely', {
      p_member_id: memberId
    });

  if (error) {
    console.error('[removeMember] ❌ Database error:', error);
    throw error;
  }

  secureLog.info('[removeMember] ✅ Member deleted from database');
  
  // Extract userId and userEmail from response
  const result = data as any;
  const userId = result?.user_id;
  const userEmail = result?.user_email;
  
  secureLog.debug('[removeMember] Processing storage deletion for user');

  // Step 2: Delete all files from storage buckets using email from SQL response
  secureLog.info('[removeMember] Step 2: Deleting user storage files...');
  
  try {
    const { data: buckets } = await supabase.storage.listBuckets();
    
    if (buckets) {
      // Sanitize email for path matching
      const sanitizedEmail = userEmail 
        ? userEmail.toLowerCase().replace(/@/g, '-at-').replace(/\./g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')
        : null;
      
      // Try common path patterns
      const userPaths = [
        sanitizedEmail ? `${sanitizedEmail}/` : null,
        `${userId}/`,
        `user_${userId}/`,
        `users/${userId}/`,
      ].filter(Boolean) as string[];
      
      for (const bucket of buckets) {
        for (const path of userPaths) {
          try {
            // Recursive function to delete all files in a folder
            const deleteFolder = async (folderPath: string): Promise<number> => {
              let totalDeleted = 0;
              const { data: items } = await supabase.storage.from(bucket.name).list(folderPath);
              
              if (!items || items.length === 0) return 0;
              
              const files = items.filter(item => item.id);
              const folders = items.filter(item => !item.id);
              
              if (files.length > 0) {
                const filePaths = files.map(file => `${folderPath}${file.name}`);
                const { error } = await supabase.storage.from(bucket.name).remove(filePaths);
                if (!error) {
                  totalDeleted += filePaths.length;
                  console.log(`[removeMember] ✅ Deleted ${filePaths.length} files from ${bucket.name}/${folderPath}`);
                }
              }
              
              for (const folder of folders) {
                totalDeleted += await deleteFolder(`${folderPath}${folder.name}/`);
              }
              
              return totalDeleted;
            };
            
            const deletedCount = await deleteFolder(path);
            if (deletedCount > 0) {
              console.log(`[removeMember] ✅ Total deleted from ${bucket.name}/${path}: ${deletedCount} files`);
            }
          } catch (err) {
            console.error(`[removeMember] Error deleting from ${bucket.name}/${path}:`, err);
          }
        }
        
        // Also delete files with userId in filename
        const { data: allFiles } = await supabase.storage.from(bucket.name).list();
        if (allFiles) {
          const userFiles = allFiles.filter(file => 
            file.name.includes(userId) || file.name.includes(userId.replace(/-/g, '_'))
          );
          if (userFiles.length > 0) {
            await supabase.storage.from(bucket.name).remove(userFiles.map(f => f.name));
            console.log(`[removeMember] ✅ Deleted ${userFiles.length} user-named files from ${bucket.name}`);
          }
        }
      }
    }
  } catch (storageError) {
    console.error('[removeMember] ⚠️ Error deleting storage files:', storageError);
  }

  // Log final status
  if (result?.deleted_from_auth) {
    console.log('[removeMember] 🔥 User account and all data deleted completely');
  } else {
    console.log('[removeMember] ℹ️ User kept in Auth (member of other organizations)');
  }
};

/**
 * Update member role (super admin only)
 */
export const updateMemberRole = async (
  memberId: string,
  newRole: UserRole
): Promise<void> => {
  // Use SECURITY DEFINER function to bypass RLS and avoid infinite recursion
  const { error } = await supabase
    .rpc('update_organization_member', {
      p_member_id: memberId,
      p_role: newRole
    });

  if (error) throw error;
};

// ============================================================================
// Invitation Operations
// ============================================================================

/**
 * Generate a unique invitation token
 */
const generateInvitationToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Create and send team invitation (super admin only)
 */
export const inviteTeamMember = async (
  email: string,
  role: import('../types/team').UserRole = 'team_member'
): Promise<TeamInvitation> => {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  const org = await getUserOrganization();
  if (!org) throw new Error('No organization found');

  const isAdmin = await isSuperAdmin(org.id);
  if (!isAdmin) throw new Error('Only super admins can invite members');

  // Validate role - only allow team roles (not super_admin)
  if (!['team_leader', 'team_member', 'team_admin'].includes(role)) {
    throw new Error('Invalid role. Can only invite: team_leader, team_member, or team_admin');
  }

  // Get current members list
  const members = await getOrganizationMembers();

  // Check if email is already a member
  const existingMember = members.find((m: OrganizationMember) => m.email === email);
  if (existingMember) {
    throw new Error('This email is already a member of your organization');
  }

  // Check if organization has reached member limit
  // Use can_add_member function which accounts for base + purchased additional slots
  const { data: canAdd, error: capacityError } = await supabase
    .rpc('can_add_member', { org_id: org.id });

  if (capacityError) {
    console.error('[inviteTeamMember] Error checking member capacity:', capacityError);
    throw new Error('Failed to check member capacity');
  }

  // Only check limit if the new role counts toward it (team_admin doesn't count)
  if (role !== 'team_admin' && !canAdd) {
    // Get slot info for better error message
    const { data: totalSlots } = await supabase
      .rpc('get_total_member_slots', { p_organization_id: org.id });
    const { data: currentCount } = await supabase
      .rpc('get_current_member_count', { p_organization_id: org.id });
    
    throw new Error(
      `Member limit reached (${currentCount}/${totalSlots} slots). ` +
      `Purchase additional member slots at £49/user to add more team members.`
    );
  }

  // Check if there's already a pending invitation using RPC
  const { data: existingInvitations, error: inviteCheckError } = await supabase
    .rpc('check_existing_invitation', {
      p_organization_id: org.id,
      p_invited_email: email
    });

  if (inviteCheckError) {
    console.error('Error checking existing invitation:', inviteCheckError);
    throw inviteCheckError;
  }

  if (existingInvitations && existingInvitations.length > 0) {
    throw new Error('An invitation has already been sent to this email');
  }

  // Create invitation
  const token = generateInvitationToken();
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

  secureLog.debug('[inviteTeamMember] Token generated and email invitation created');
  secureLog.debug('[inviteTeamMember] Expires at:', expiresAt.toISOString());
  secureLog.email('[inviteTeamMember] Invited:', email);

  // Create invitation using RPC
  const { data, error } = await supabase
    .rpc('create_team_invitation', {
      p_organization_id: org.id,
      p_invited_email: email,
      p_invitation_token: token,
      p_expires_at: expiresAt.toISOString(),
      p_role: role
    });

  if (error) {
    console.error('[inviteTeamMember] ❌ RPC error:', error);
    throw error;
  }

  if (!data) {
    throw new Error('Failed to create invitation - no data returned');
  }

  secureLog.info('[inviteTeamMember] ✅ Invitation created in database', {
    id: data.id,
    organization: org.id
  });

  // Send invitation email via backend (non-blocking - don't fail if email service is down)
  try {
    secureLog.info('[inviteTeamMember] 📤 Sending invitation email...');
    await sendInvitationEmail(email, token, org.name);
    secureLog.info('[inviteTeamMember] ✅ Email sent successfully');
  } catch (emailError) {
    secureLog.warn('[inviteTeamMember] ⚠️ Email failed to send (invitation still created)');
    // Don't throw - invitation was created successfully in database
    // User can manually share the invite link or resend later
  }

  return data;
};

/**
 * Send invitation email via backend
 */
const sendInvitationEmail = async (
  email: string,
  token: string,
  organizationName: string
): Promise<void> => {
  const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || 'https://noorcad-api.vercel.app').replace(/\/$/, '');
  
  try {
    const response = await fetch(`${apiBaseUrl}/send-team-invitation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, token, organizationName })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send invitation email');
    }
  } catch (error) {
    console.error('Error sending invitation email:', error);
    throw new Error('Failed to send invitation email');
  }
};

/**
 * Get all invitations for user's organization
 */
export const getOrganizationInvitations = async (): Promise<TeamInvitationWithDetails[]> => {
  const org = await getUserOrganization();
  if (!org) return [];

  // Use RPC function to get invitations with inviter details
  const { data, error } = await supabase
    .rpc('get_organization_invitations', { org_id: org.id });

  if (error) throw error;
  
  // Transform to add friendly names and filter to only show pending invitations
  return (data || [])
    .filter((inv: any) => inv.status === 'pending') // Only show pending invitations
    .map((inv: any) => {
      const firstName = inv.invited_by_metadata?.first_name || '';
      const lastName = inv.invited_by_metadata?.last_name || '';
      const invitedByName = `${firstName} ${lastName}`.trim() || inv.invited_by_email || 'Admin';
      
      return {
        ...inv,
        invited_by_name: invitedByName,
        organization_name: org.name
      };
    });
};

/**
 * Revoke an invitation (super admin only) - DELETES the invitation
 */
export const revokeInvitation = async (invitationId: string): Promise<void> => {
  console.log('[TEAM] Revoking invitation:', invitationId);
  
  const org = await getUserOrganization();
  if (!org) throw new Error('No organization found');

  const isAdmin = await isSuperAdmin(org.id);
  if (!isAdmin) throw new Error('Only super admins can revoke invitations');

  console.log('[TEAM] User is admin, proceeding with deletion');
  console.log('[TEAM] Organization ID:', org.id);
  console.log('[TEAM] Invitation ID:', invitationId);
  
  // DELETE the invitation instead of updating status
  const { data: deleteData, error } = await supabase
    .from('team_invitations')
    .delete()
    .eq('id', invitationId)
    .eq('organization_id', org.id)
    .select(); // Return deleted rows

  console.log('[TEAM] Delete response:', { deleteData, error });

  if (error) {
    console.error('[TEAM] ❌ Error deleting invitation:', error);
    console.error('[TEAM] Error details:', JSON.stringify(error, null, 2));
    throw new Error(`Failed to delete invitation: ${error.message}`);
  }
  
  if (!deleteData || deleteData.length === 0) {
    console.error('[TEAM] ⚠️ No rows deleted! Invitation might not exist or RLS blocked it.');
    
    // Verify invitation still exists
    const { data: checkData } = await supabase
      .from('team_invitations')
      .select('*')
      .eq('id', invitationId);
    
    console.log('[TEAM] Verification check - invitation still exists:', checkData);
    
    throw new Error('Invitation could not be deleted. This may be a permissions issue. Please check RLS policies.');
  }
  
  console.log('[TEAM] ✅ Invitation deleted successfully:', deleteData.length, 'row(s)');
};

/**
 * Validate and get invitation details by token
 */
export const getInvitationByToken = async (
  token: string
): Promise<TeamInvitationWithDetails | null> => {
  console.log('[getInvitationByToken] 🔍 Looking up token:', token);
  
  // Direct query instead of RPC to avoid 406 errors
  const { data, error } = await supabase
    .from('team_invitations')
    .select(`
      *,
      organization:organization_id (
        name,
        company_name
      )
    `)
    .eq('invitation_token', token) // Column name is 'invitation_token'
    .eq('status', 'pending')
    .maybeSingle(); // Use maybeSingle() to avoid 406 error when no invitation found

  if (error) {
    console.error('[getInvitationByToken] ❌ Database query error:', error);
    return null;
  }
  
  if (!data) {
    console.error('[getInvitationByToken] ❌ No invitation found for token');
    console.log('[getInvitationByToken] 🔎 Searching for ANY invitation with this token (ignoring status)...');
    
    // Debug: Check if invitation exists with any status
    const { data: debugData } = await supabase
      .from('team_invitations')
      .select('id, invitation_token, status, expires_at, invited_email')
      .eq('invitation_token', token)
      .maybeSingle();
    
    if (debugData) {
      console.log('[getInvitationByToken] 🐛 Found invitation with different status:', {
        id: debugData.id,
        status: debugData.status,
        expires_at: debugData.expires_at,
        email: debugData.invited_email
      });
    } else {
      console.log('[getInvitationByToken] 🐛 Token does not exist in database at all');
    }
    
    return null;
  }

  console.log('[getInvitationByToken] ✅ Found invitation:', {
    id: data.id,
    email: data.invited_email,
    organization_id: data.organization_id,
    expires_at: data.expires_at,
    status: data.status
  });

  // Check if expired
  const now = new Date();
  const expiresAt = new Date(data.expires_at);
  console.log('[getInvitationByToken] ⏰ Time check:', {
    now: now.toISOString(),
    expires_at: expiresAt.toISOString(),
    is_expired: now > expiresAt
  });
  
  if (now > expiresAt) {
    // Mark as expired
    console.log('[getInvitationByToken] ⚠️ Marking invitation as expired');
    await supabase
      .from('team_invitations')
      .update({ status: 'expired' })
      .eq('id', data.id);
    
    console.error('[getInvitationByToken] ❌ Invitation has expired');
    return null;
  }

  console.log('[getInvitationByToken] ✅ Invitation is valid and not expired');

  // Return with organization name
  const result = {
    ...data,
    organization_name: data.organization?.name || data.organization?.company_name || 'Unknown Organization'
  };
  
  console.log('[getInvitationByToken] 📦 Returning invitation with organization:', result.organization_name);
  
  return result;
};

/**
 * Accept invitation and join organization
 */
export const acceptInvitation = async (
  token: string,
  userId: string
): Promise<void> => {
  console.log('[acceptInvitation] 🎯 Starting acceptance process:', { token, userId });
  
  const invitation = await getInvitationByToken(token);
  if (!invitation) {
    console.error('[acceptInvitation] ❌ Invitation not found or invalid');
    throw new Error('Invalid or expired invitation');
  }

  console.log('[acceptInvitation] ✅ Invitation validated:', {
    id: invitation.id,
    email: invitation.invited_email,
    organization: invitation.organization_name
  });

  // Check if user's email matches invitation
  const { data: { user } } = await supabase.auth.getUser();
  console.log('[acceptInvitation] 👤 Current user email:', user?.email);
  console.log('[acceptInvitation] 📧 Invited email:', invitation.invited_email);
  
  if (user?.email !== invitation.invited_email) {
    console.error('[acceptInvitation] ❌ Email mismatch!');
    throw new Error('This invitation was sent to a different email address');
  }

  console.log('[acceptInvitation] ✅ Email matches, creating organization membership...');

  // Create organization membership with the role specified in the invitation
  const { error: memberError } = await supabase
    .from('organization_members')
    .insert({
      organization_id: invitation.organization_id,
      user_id: userId,
      role: invitation.invited_role || 'team_member', // Use invited_role from invitation
      is_active: true
    });

  if (memberError) {
    console.error('[acceptInvitation] ❌ Failed to create membership:', memberError);
    throw memberError;
  }

  console.log('[acceptInvitation] ✅ Membership created successfully');

  // DELETE the invitation using RPC to bypass RLS
  console.log('[acceptInvitation] 🗑️ Deleting invitation (membership already created)...');
  const { error: inviteError } = await supabase
    .rpc('delete_invitation_after_acceptance', {
      p_invitation_id: invitation.id,
      p_user_id: userId
    });

  if (inviteError) {
    console.error('[acceptInvitation] ❌ Failed to delete invitation:', inviteError);
    // Don't throw error here, membership was created successfully
  } else {
    console.log('[acceptInvitation] ✅ Invitation deleted successfully');
  }

  console.log('[acceptInvitation] 🎉 Invitation accepted successfully!');
};

/**
 * Check if email has pending invitation
 */
export const checkPendingInvitation = async (
  email: string
): Promise<TeamInvitationWithDetails | null> => {
  const { data, error } = await supabase
    .from('team_invitations')
    .select(`
      *,
      organization:organization_id (
        name,
        company_name
      )
    `)
    .eq('invited_email', email)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle(); // Use maybeSingle() to avoid 406 error when no invitation found

  if (error) {
    console.error('[checkPendingInvitation] Error:', error);
    return null;
  }
  
  if (!data) return null;

  // Check if expired
  const now = new Date();
  const expiresAt = new Date(data.expires_at);
  if (now > expiresAt) {
    return null;
  }

  return {
    ...data,
    organization_name: data.organization?.name
  };
};
